import React, { useEffect, useState } from "react";
import { Container, Modal, Form, Button, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { message, Modal as AntModal } from "antd";
import Footer from "../../../components/superAdmin/footer/Footer";
import SubAdminTable from "../../../components/subadmin/SubAdminTable";
import ShowDetailsModal from "../../../common/ShowDetailsModal";
import SubAdminHeader from "../components/SubAdminHeader";

const SubAdminVisitors = () => {
    const navigate = useNavigate();
    const [visitors, setVisitors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState(null);
    
    // Modals state
    const [showDetails, setShowDetails] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showEdit, setShowEdit] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('jff_token');
            const dashResponse = await axios.get(`${process.env.REACT_APP_API_END_POINT}/subadmin/dashboard`, {
                headers: { token }
            });
            const perms = dashResponse.data.permissions.visitorsList;
            setPermissions(perms);

            if (!perms.enabled || !perms.read) {
                navigate("/subadmin/dashboard");
                return;
            }

            const response = await axios.get(`${process.env.REACT_APP_API_END_POINT}/subadmin/visitors`, {
                headers: { token }
            });
            setVisitors(response.data.data);
        } catch (error) {
            console.error("Error fetching visitors:", error);
            if (error.response && error.response.status === 403) {
                navigate("/subadmin/dashboard");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [navigate]);

    const handleView = (item) => {
        setSelectedItem(item);
        setShowDetails(true);
    };

    const handleEdit = (item) => {
        setEditForm({ ...item });
        setShowEdit(true);
    };

    const handleUpdate = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('jff_token');
            const formData = new FormData();
            Object.keys(editForm).forEach(key => {
                if (key !== 'photo' && key !== 'id_proof_front' && key !== 'id_proof_back') {
                    formData.append(key, editForm[key]);
                }
            });
            formData.append('id', editForm._id);

            await axios.post(`${process.env.REACT_APP_API_END_POINT}/update-visitor`, formData, {
                headers: { token, barcode: editForm.barcode }
            });
            message.success("Updated successfully");
            setShowEdit(false);
            fetchData();
        } catch (error) {
            message.error("Failed to update");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (id) => {
        AntModal.confirm({
            title: 'Are you sure you want to delete this visitor?',
            content: 'This action cannot be undone.',
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const token = localStorage.getItem('jff_token');
                    await axios.post(`${process.env.REACT_APP_API_END_POINT}/delete-visitor`, { id }, {
                        headers: { token }
                    });
                    message.success("Deleted successfully");
                    fetchData();
                } catch (error) {
                    message.error("Failed to delete");
                }
            }
        });
    };

    const handleStatusChange = async (item) => {
        try {
            const token = localStorage.getItem('jff_token');
            let newStatus = item.status === "active" ? "inactive" : "active";
            
            await axios.post(`${process.env.REACT_APP_API_END_POINT}/update-visitor`, 
                { 
                    id: item._id, 
                    status: newStatus,
                    visitor_name: item.visitor_name,
                    mobile_number: item.mobile_number,
                    barcode: item.barcode 
                },
                { headers: { token } }
            );
            
            message.success("Status updated successfully");
            fetchData();
        } catch (error) {
            console.error("Error updating status:", error);
            message.error("Failed to update status");
        }
    };

    return (
        <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
            <SubAdminHeader />

            <Container fluid className="px-0 py-5">
                <div className="text-center mb-4">
                    <h2 className="form-heading">Visitors List - {visitors.length}</h2>
                </div>
                <SubAdminTable 
                    data={visitors}
                    columns={permissions?.columns || []}
                    loading={loading}
                    writeAccess={permissions?.write}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onStatusChange={handleStatusChange}
                    tableName="Visitors List"
                />
            </Container>

            <ShowDetailsModal 
                showAllDetails={showDetails} 
                setShowAllDetails={setShowDetails} 
                data={selectedItem} 
                allowedColumns={permissions?.columns}
            />

            <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Visitor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control 
                                        value={editForm.visitor_name || ''} 
                                        onChange={(e) => setEditForm({ ...editForm, visitor_name: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Mobile</Form.Label>
                                    <Form.Control 
                                        value={editForm.mobile_number || ''} 
                                        onChange={(e) => setEditForm({ ...editForm, mobile_number: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Company</Form.Label>
                                    <Form.Control 
                                        value={editForm.company_name || ''} 
                                        onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control 
                                        value={editForm.email || ''} 
                                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleUpdate} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Footer />
        </div>
    );
};

export default SubAdminVisitors;
